import { useEffect, useRef } from "react";

export const HeroCanvas = () => {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current;
    const ctx = cv.getContext("2d");
    let W, H, off, octx, nodes = [], mts = [], stars = [], mists = [], flies = [], tris = [], t = 0, mx = -999, my = -999, alive = true, raf;
    const dpr = window.devicePixelRatio || 1;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const TAU = Math.PI * 2;

    const build = () => {
      let seed = 77;
      const rand = () => (seed = (seed * 9301 + 49297) % 233280) / 233280;
      const n = Math.max(8, Math.floor((W * 0.6) / (180 * dpr)));
      nodes = Array.from({ length: n }, () => ({
        x: Math.random() * W * 0.62, y: Math.random() * H * 0.85,
        vx: (Math.random() - 0.5) * 0.22 * dpr, vy: (Math.random() - 0.5) * 0.18 * dpr,
        w: (46 + Math.random() * 36) * dpr, h: (22 + Math.random() * 10) * dpr }));
      mts = [0, 1, 2, 3].map((li) => Array.from({ length: 17 }, () => H * (0.38 + li * 0.13) + (rand() - 0.5) * H * (0.26 - li * 0.05)));
      stars = Array.from({ length: 110 }, () => ({ x: rand(), y: rand() * 0.55, r: 0.4 + rand() * 1.3, p: rand() * TAU }));
      mists = Array.from({ length: 6 }, () => ({ y: 0.5 + rand() * 0.42, sp: 10 + rand() * 20, o: 0.04 + rand() * 0.05, w: 0.3 + rand() * 0.35, ph: rand() }));
      flies = Array.from({ length: 16 }, () => ({ x: 0.66 + rand() * 0.34, sp: 0.014 + rand() * 0.028, ph: rand(), wob: rand() * TAU, r: (0.9 + rand() * 1.3) * dpr }));
      tris = Array.from({ length: 14 }, () => ({ py: rand(), sp: 0.05 + rand() * 0.09, ph: rand(), s: (7 + rand() * 15) * dpr, rot: rand() * TAU, rs: (rand() - 0.5) * 0.9 }));
    };
    const resize = () => {
      W = cv.width = cv.offsetWidth * dpr; H = cv.height = cv.offsetHeight * dpr;
      off = document.createElement("canvas"); off.width = W; off.height = H; octx = off.getContext("2d");
      build();
    };
    const onMove = (e) => { const r = cv.getBoundingClientRect(); mx = (e.clientX - r.left) * dpr; my = (e.clientY - r.top) * dpr; };
    const cubic = (p0, p1, p2, p3, u) => { const v = 1 - u; return v * v * v * p0 + 3 * v * v * u * p1 + 3 * v * u * u * p2 + u * u * u * p3; };
    const rr = (c, x, y, w, h, r) => { c.beginPath(); c.moveTo(x + r, y); c.arcTo(x + w, y, x + w, y + h, r); c.arcTo(x + w, y + h, x, y + h, r); c.arcTo(x, y + h, x, y, r); c.arcTo(x, y, x + w, y, r); c.closePath(); };

    const dTop = () => W * 0.80, dBot = () => W * 0.62;
    const diagX = (y) => dTop() + (dBot() - dTop()) * (y / H);

    const drawNature = (c) => {
      const sky = c.createLinearGradient(0, 0, 0, H);
      sky.addColorStop(0, "#070b12"); sky.addColorStop(0.35, "#0b1c2e"); sky.addColorStop(0.62, "#0e2f38"); sky.addColorStop(0.82, "#123a30"); sky.addColorStop(1, "#0a1f1a");
      c.fillStyle = sky; c.fillRect(0, 0, W, H);
      for (let i = 0; i < 2; i++) {
        c.beginPath();
        const baseY = H * (0.16 + i * 0.1);
        for (let x = 0; x <= W; x += W / 40) {
          const y = baseY + Math.sin(x / W * 5 + t * (0.5 + i * 0.3) + i * 2) * H * 0.045 + Math.sin(x / W * 11 - t * 0.4) * H * 0.02;
          x === 0 ? c.moveTo(x, y) : c.lineTo(x, y);
        }
        c.lineTo(W, 0); c.lineTo(0, 0); c.closePath();
        const ag = c.createLinearGradient(0, 0, 0, H * 0.35);
        ag.addColorStop(0, i ? "rgba(56,182,255,0.10)" : "rgba(82,210,115,0.12)");
        ag.addColorStop(1, "rgba(82,210,115,0)");
        c.fillStyle = ag; c.fill();
      }
      stars.forEach((s) => {
        c.fillStyle = `rgba(225,245,255,${0.2 + 0.5 * Math.abs(Math.sin(t * 1.5 + s.p))})`;
        c.beginPath(); c.arc(W * s.x, H * s.y, s.r * dpr, 0, TAU); c.fill();
      });
      const moX = W * 0.87, moY = H * 0.18, moR = H * 0.06;
      const bloom = c.createRadialGradient(moX, moY, moR * 0.3, moX, moY, moR * 6);
      bloom.addColorStop(0, "rgba(190,235,255,.4)"); bloom.addColorStop(0.4, "rgba(140,210,235,.12)"); bloom.addColorStop(1, "rgba(140,210,235,0)");
      c.fillStyle = bloom; c.fillRect(moX - moR * 6, moY - moR * 6, moR * 12, moR * 12);
      const mg = c.createRadialGradient(moX - moR * 0.35, moY - moR * 0.35, moR * 0.2, moX, moY, moR);
      mg.addColorStop(0, "#eafaff"); mg.addColorStop(1, "#9fd2d8");
      c.fillStyle = mg; c.beginPath(); c.arc(moX, moY, moR, 0, TAU); c.fill();
      const hg = c.createLinearGradient(0, H * 0.4, 0, H * 0.72);
      hg.addColorStop(0, "rgba(90,220,200,0)"); hg.addColorStop(1, "rgba(90,220,200,.14)");
      c.fillStyle = hg; c.fillRect(0, H * 0.4, W, H * 0.32);
      const cols = ["#1b4152", "#153a41", "#0f3028", "#081d16"];
      mts.forEach((pts, li) => {
        c.fillStyle = cols[li];
        c.beginPath(); c.moveTo(0, H);
        pts.forEach((y, i) => c.lineTo((i / (pts.length - 1)) * W, y));
        c.lineTo(W, H); c.closePath(); c.fill();
        if (li < 2) {
          c.fillStyle = `rgba(120,225,235,${0.05 - li * 0.02})`;
          c.beginPath(); c.moveTo(0, H);
          pts.forEach((y, i) => c.lineTo((i / (pts.length - 1)) * W, y + 3 * dpr));
          c.lineTo(W, H); c.closePath(); c.fill();
        }
      });
      mists.forEach((m) => {
        const bw = W * m.w;
        const offx = ((t * m.sp * dpr + m.ph * (W + bw)) % (W + bw)) - bw;
        const g = c.createLinearGradient(W - offx - bw, 0, W - offx, 0);
        g.addColorStop(0, "rgba(150,225,210,0)"); g.addColorStop(0.5, `rgba(150,225,210,${m.o})`); g.addColorStop(1, "rgba(150,225,210,0)");
        c.fillStyle = g; c.fillRect(W - offx - bw, H * m.y - H * 0.045, bw, H * 0.09);
      });
      const cx = W * 0.78, cy = H * 0.6;
      c.fillStyle = "#04090c";
      c.beginPath();
      c.moveTo(cx - W * 0.1, H); c.lineTo(cx - W * 0.03, cy + H * 0.03); c.lineTo(cx + W * 0.07, cy);
      c.lineTo(cx + W * 0.1, cy + H * 0.08); c.lineTo(cx + W * 0.17, H); c.closePath(); c.fill();
      const px = cx + W * 0.04, py = cy + H * 0.002, s = H * 0.08, sway = Math.sin(t * 2) * s * 0.03;
      c.fillStyle = "#03060a";
      c.beginPath(); c.arc(px, py - s * 0.84, s * 0.11, 0, TAU); c.fill();
      c.beginPath();
      c.moveTo(px - s * 0.13, py); c.lineTo(px - s * 0.11, py - s * 0.64);
      c.quadraticCurveTo(px, py - s * 0.8, px + s * 0.12, py - s * 0.62); c.lineTo(px + s * 0.15, py);
      c.closePath(); c.fill();
      c.strokeStyle = "#03060a"; c.lineWidth = s * 0.055; c.lineCap = "round";
      c.beginPath(); c.moveTo(px + s * 0.1, py - s * 0.52); c.quadraticCurveTo(px + s * 0.32 + sway, py - s * 0.46, px + s * 0.4 + sway * 2, py - s * 0.2); c.stroke();
      c.strokeStyle = "rgba(150,235,255,.5)"; c.lineWidth = 1.1 * dpr;
      c.beginPath(); c.moveTo(px + s * 0.12, py - s * 0.66); c.quadraticCurveTo(px + s * 0.16, py - s * 0.35, px + s * 0.15, py - s * 0.05); c.stroke();
      flies.forEach((f) => {
        const prog = (t * f.sp + f.ph) % 1;
        const fy = H * (0.92 - prog * 0.4);
        const fx = W * f.x + Math.sin(t * 1.7 + f.wob) * W * 0.01;
        c.fillStyle = `rgba(140,235,160,${0.5 * Math.abs(Math.sin(t * 2.2 + f.wob)) * (1 - prog * 0.55)})`;
        c.beginPath(); c.arc(fx, fy, f.r, 0, TAU); c.fill();
      });
    };

    const drawBlueprint = (c) => {
      c.clearRect(0, 0, W, H);
      c.fillStyle = "#0a0e13"; c.fillRect(0, 0, W, H);
      c.strokeStyle = "rgba(56,182,255,.05)"; c.lineWidth = 1;
      const g = 64 * dpr;
      for (let x = 0; x < W; x += g) { c.beginPath(); c.moveTo(x, 0); c.lineTo(x, H); c.stroke(); }
      for (let y = 0; y < H; y += g) { c.beginPath(); c.moveTo(0, y); c.lineTo(W, y); c.stroke(); }
      nodes.forEach((nd) => {
        nd.x += nd.vx; nd.y += nd.vy;
        if (nd.x < -80 || nd.x > W * 0.76) nd.vx *= -1;
        if (nd.y < -40 || nd.y > H + 40) nd.vy *= -1;
        const dx = nd.x - mx, dy = nd.y - my, d = Math.hypot(dx, dy);
        if (d < 180 * dpr && d > 1) { nd.x += (dx / d) * 0.9; nd.y += (dy / d) * 0.9; }
      });
      for (let i = 0; i < nodes.length; i++)
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i], b = nodes[j], d = Math.hypot(a.x - b.x, a.y - b.y), max = 300 * dpr;
          if (d < max) {
            const o = (1 - d / max) * 0.5;
            c.strokeStyle = `rgba(56,182,255,${o})`; c.lineWidth = 1.4 * dpr;
            c.beginPath(); c.moveTo(a.x + a.w / 2, a.y);
            c.bezierCurveTo(a.x + a.w / 2 + 60 * dpr, a.y, b.x - b.w / 2 - 60 * dpr, b.y, b.x - b.w / 2, b.y); c.stroke();
            const p = (t * 2 + i * 0.7 + j * 0.3) % 1;
            const qx = cubic(a.x + a.w / 2, a.x + a.w / 2 + 60 * dpr, b.x - b.w / 2 - 60 * dpr, b.x - b.w / 2, p);
            const qy = cubic(a.y, a.y, b.y, b.y, p);
            c.fillStyle = `rgba(140,220,255,${o + 0.25})`; c.beginPath(); c.arc(qx, qy, 2.6 * dpr, 0, TAU); c.fill();
          }
        }
      nodes.forEach((nd) => {
        c.fillStyle = "rgba(14,26,40,.88)"; c.strokeStyle = "rgba(56,182,255,.55)"; c.lineWidth = 1.4 * dpr;
        rr(c, nd.x - nd.w / 2, nd.y - nd.h / 2, nd.w, nd.h, 6 * dpr); c.fill(); c.stroke();
        c.fillStyle = "rgba(82,210,115,.9)"; c.beginPath(); c.arc(nd.x - nd.w / 2, nd.y, 3.2 * dpr, 0, TAU); c.fill();
        c.fillStyle = "rgba(255,120,71,.9)"; c.beginPath(); c.arc(nd.x + nd.w / 2, nd.y, 3.2 * dpr, 0, TAU); c.fill();
        c.fillStyle = "rgba(56,182,255,.5)"; c.fillRect(nd.x - nd.w / 2 + 5 * dpr, nd.y - nd.h / 2 + 5 * dpr, nd.w * 0.5, 3 * dpr);
      });
      const fw = W * 0.16;
      c.globalCompositeOperation = "destination-in";
      const mask = c.createLinearGradient(dTop() - fw, 0, dTop() + fw * 0.4, 0);
      mask.addColorStop(0, "rgba(0,0,0,1)"); mask.addColorStop(1, "rgba(0,0,0,0)");
      c.save();
      c.transform(1, 0, (dBot() - dTop()) / H, 1, 0, 0);
      c.fillStyle = mask;
      c.fillRect(-W, 0, W * 3, H);
      c.restore();
      c.globalCompositeOperation = "source-over";
    };

    const drawTransition = () => {
      tris.forEach((tr) => {
        const prog = (t * tr.sp + tr.ph) % 1;
        const y = H * tr.py;
        const bx = diagX(y);
        const x = bx - W * 0.1 + prog * W * 0.16;
        const rot = tr.rot + t * tr.rs;
        const fade = Math.sin(prog * Math.PI);
        const wire = prog < 0.55;
        ctx.save();
        ctx.translate(x, y + Math.sin(t * 1.4 + tr.ph * 9) * H * 0.012);
        ctx.rotate(rot);
        ctx.beginPath();
        ctx.moveTo(0, -tr.s); ctx.lineTo(tr.s * 0.87, tr.s * 0.5); ctx.lineTo(-tr.s * 0.87, tr.s * 0.5); ctx.closePath();
        if (wire) {
          ctx.strokeStyle = `rgba(56,182,255,${0.5 * fade})`;
          ctx.lineWidth = 1.1 * dpr;
          ctx.stroke();
        } else {
          ctx.fillStyle = `rgba(82,210,115,${0.3 * fade})`;
          ctx.strokeStyle = `rgba(140,235,190,${0.45 * fade})`;
          ctx.lineWidth = 1 * dpr;
          ctx.fill(); ctx.stroke();
        }
        ctx.restore();
      });
    };

    const draw = () => {
      if (!alive) return;
      t += 0.008;
      ctx.clearRect(0, 0, W, H);
      drawNature(ctx);
      drawBlueprint(octx);
      ctx.drawImage(off, 0, 0);
      drawTransition();
      if (!reduce) raf = requestAnimationFrame(draw);
    };
    resize(); draw();
    window.addEventListener("resize", resize);
    cv.parentElement.addEventListener("mousemove", onMove);
    return () => { alive = false; cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={ref} id="bpCanvas" data-testid="hero-canvas" />;
};
